import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X, ArrowUpRight } from 'lucide-react';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSolutionsOpen, setIsSolutionsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const closeTimeoutRef = useRef(null);
  const location = useLocation();

  const solutions = [
    { name: 'Term Loans', path: '/solutions/term-loans', desc: '3 to 10 year fixed rate financing' },
    { name: 'Jumbo Term Loan', path: '/solutions/jumbo-term-loan', desc: 'Up to $25M, close in 1 day' },
    { name: 'Hybrid Term / LOC', path: '/solutions/hybrid-loc', desc: 'Up to $25M, pay only what you use' },
    { name: 'Lines of Credit', path: '/solutions/lines-of-credit', desc: 'Flexible revolving capital' },
    { name: 'Equipment Financing', path: '/solutions/equipment-financing', desc: 'Fund machinery and assets' },
    { name: 'SBA Grocery Program', path: '/solutions/sba-grocery', desc: 'SBA backed, agriculture and food' },
    { name: 'SBA Made in America', path: '/solutions/sba-made-in-america', desc: 'SBA backed, U.S. manufacturing' },
    { name: 'Real Estate Finance', path: '/solutions/real-estate', desc: '$250K to $2B, USA + Caribbean' },
    { name: 'Merchant Processing', path: '/solutions/merchant-processing', desc: 'Reduce fees, improve margins' },
    { name: 'Management Systems', path: '/solutions/management-systems', desc: 'PMS, LMS and POS solutions' },
    { name: 'Transportation Finance', path: '/solutions/transportation-finance', desc: 'Revolving credit, equipment, and cash out' },
    { name: 'Foreclosure Bailout', path: '/solutions/foreclosure-bailout', desc: 'Emergency bridge for commercial properties' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsSolutionsOpen(false);
  }, [location.pathname]);

  const handleSolutionsMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsSolutionsOpen(true);
  }, []);

  const handleSolutionsMouseLeave = useCallback(() => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsSolutionsOpen(false);
    }, 250);
  }, []);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'glass shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        <div className="flex justify-between items-center h-20">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_0073d843-75f0-4073-b1ec-cb9427d27521/artifacts/vnbckdjp_QuidityDirect%20Logo.png"
              alt="Quidity Direct"
              className="h-11 w-auto"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-10">
            <Link
              to="/"
              data-testid="nav-home"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 ${
                isActive('/') ? 'text-[#115E59]' : 'text-slate-700 hover:text-[#115E59]'
              }`}
            >
              Home
            </Link>

            <div
              className="relative"
              onMouseEnter={handleSolutionsMouseEnter}
              onMouseLeave={handleSolutionsMouseLeave}
            >
              <button
                data-testid="nav-solutions-trigger"
                className={`flex items-center gap-1 text-sm font-semibold tracking-wide transition-colors duration-200 py-3 ${
                  location.pathname.startsWith('/solutions') ? 'text-[#115E59]' : 'text-slate-700 hover:text-[#115E59]'
                }`}
              >
                Solutions
                <ChevronDown className={`h-4 w-4 transition-transform duration-200 ${isSolutionsOpen ? 'rotate-180' : ''}`} />
              </button>

              {isSolutionsOpen && (
                <div
                  data-testid="solutions-dropdown"
                  className="absolute top-full left-1/2 -translate-x-1/2 w-80 bg-white rounded-xl shadow-2xl border border-slate-100 py-3 z-50 animate-scale-in"
                  style={{ animationDuration: '0.15s' }}
                  onMouseEnter={handleSolutionsMouseEnter}
                  onMouseLeave={handleSolutionsMouseLeave}
                >
                  {solutions.map((s) => (
                    <Link
                      key={s.path}
                      to={s.path}
                      data-testid={`dropdown-${s.path.split('/').pop()}`}
                      className="block px-5 py-3 hover:bg-slate-50 transition-colors group"
                    >
                      <span className="text-sm font-semibold text-slate-800 group-hover:text-[#115E59] transition-colors">
                        {s.name}
                      </span>
                      <span className="block text-xs text-slate-400 mt-0.5">{s.desc}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              data-testid="nav-about"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 ${
                isActive('/about') ? 'text-[#115E59]' : 'text-slate-700 hover:text-[#115E59]'
              }`}
            >
              About
            </Link>
            <Link
              to="/academy"
              data-testid="nav-academy"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 ${
                location.pathname.startsWith('/academy') ? 'text-[#115E59]' : 'text-slate-700 hover:text-[#115E59]'
              }`}
            >
              Academy
            </Link>
            <Link
              to="/contact"
              data-testid="nav-contact"
              className={`text-sm font-semibold tracking-wide transition-colors duration-200 ${
                isActive('/contact') ? 'text-[#115E59]' : 'text-slate-700 hover:text-[#115E59]'
              }`}
            >
              Contact
            </Link>
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://portal.quidity.fund/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="portal-login-button"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-[#115E59] transition-colors px-4 py-2"
            >
              Log In
              <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
            <Link
              to="/partner"
              data-testid="nav-partner-cta"
              className="text-sm font-semibold text-[#115E59] border border-[#115E59] rounded-sm px-5 py-2.5 hover:bg-[#115E59] hover:text-white transition-all duration-200"
            >
              Become a Partner
            </Link>
            <Link
              to="/apply"
              data-testid="nav-apply-cta"
              className="text-sm font-semibold bg-slate-900 text-white rounded-sm px-5 py-2.5 hover:bg-slate-800 transition-all duration-200"
            >
              Apply Now
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {isMenuOpen && (
          <div data-testid="mobile-menu" className="lg:hidden border-t border-slate-200 py-6 bg-white animate-fade-in" style={{ animationDuration: '0.2s' }}>
            <div className="space-y-1">
              <Link to="/" className="block py-3 px-2 text-sm font-semibold text-slate-800 hover:text-[#115E59]">Home</Link>
              <div className="py-2 px-2">
                <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">Solutions</div>
                {solutions.map((s) => (
                    <Link key={s.path} to={s.path} className="block py-2 pl-3 text-sm text-slate-600 hover:text-[#115E59]">
                      {s.name}
                    </Link>
                ))}
              </div>
              <Link to="/about" className="block py-3 px-2 text-sm font-semibold text-slate-800 hover:text-[#115E59]">About</Link>
              <Link to="/academy" className="block py-3 px-2 text-sm font-semibold text-slate-800 hover:text-[#115E59]">Academy</Link>
              <Link to="/contact" className="block py-3 px-2 text-sm font-semibold text-slate-800 hover:text-[#115E59]">Contact</Link>
              <div className="pt-4 flex flex-col gap-3">
                <Link to="/partner" className="block text-center text-sm font-semibold text-[#115E59] border border-[#115E59] rounded-sm px-5 py-2.5">Become a Partner</Link>
                <Link to="/apply" className="block text-center text-sm font-semibold bg-slate-900 text-white rounded-sm px-5 py-2.5">Apply Now</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
